import { cx } from "@suse-o11y/class-variance";
import { Signal, untracked, useComputed } from "@suse-o11y/signal";
import { usePermanent } from "@suse-o11y/use-permanent";
import React from "react";

import { Dropdown, type DropdownProps } from "~/components/dropdown";

import type { ButtonProps } from "../react/buttons/Button";

import { DropdownWithHostProvider } from "./dropdown-with-host-provider";

type InheritedDropdownProps = Pick<
	DropdownProps,
	"destroyPopupOnHide" | "placement" | "overlayStyle" | "overlayClassName" | "overlay" | "trigger" | "align"
>;

type InheritedButtonProps = Pick<ButtonProps, "type">;

export interface DropdownWithHostProps extends InheritedDropdownProps, InheritedButtonProps {
	className?: string;
	visible?: boolean;
	isOpen?: Signal<boolean>;
	children: React.ReactNode;

	triggerElement: React.ReactNode;
}

export const DropdownWithHost: React.FC<DropdownWithHostProps> = ({
	className,
	visible = true,
	type = "default",
	isOpen: isOpenProp,
	children,
	triggerElement,
	...props
}) => {
	const isOpenFallback = usePermanent(() => Signal(false));
	const isOpen = isOpenProp ?? isOpenFallback;
	const isOpenValue = useComputed((monitor) => isOpen.read(monitor), [isOpen]);

	if (!visible) {
		return children;
	}

	return (
		<DropdownWithHostProvider isOpen={isOpen}>
			<Dropdown
				onVisibleChange={(isVisible) => {
					isOpen.write(isVisible);
				}}
				visible={isOpenValue}
				{...props}
			>
				<div className={cx("flex", className)}>
					<div
						className="contents"
						onClick={(event) => {
							// stops the dropdown from opening when clicking on the host
							// but closes the dropdown when clicking on the host
							if (!untracked(isOpen)) {
								event.stopPropagation();
							}
						}}
					>
						{children}
					</div>

					<span className={cx("w-px", type === "primary" && "bg-primary-500")}>{/* gap */}</span>

					{triggerElement}
				</div>
			</Dropdown>
		</DropdownWithHostProvider>
	);
};
